import styled, { css } from 'styled-components';
import {
    BorderRadiusMedium,
    Spacing4,
    Spacing12,
    Spacing16,
    Spacing24,
    Spacing32,
    Spacing48
} from 'autogen/design-tokens/tokens';
import type { ExtendedPlaceholderHeaderModel, MatasPlusOfferNudgeModel } from 'autogen/swagger/all';
import Countdown from 'components/countdown';
import InlinePageActionButton from 'components/generic/InlineButton/InlinePageActionButton';
import Picture from 'components/generic/picture';
import mapResponsiveImageModelToPictureProps from 'components/generic/picture/helpers/mappingHelper';
import { Body, Headline, Label } from 'components/generic/typography';
import Layout from 'components/Layout';
import PlusOfferNudge from 'components/matasPlus/PlusOfferNudge';
import PersonalOfferToggle from 'components/pages/PLP/components/PersonalOfferToggle';
import DisclaimerButton from 'components/pages/placeholders/shared/DisclaimerButton';
import PageAction from 'components/shared/Routing/PageAction';
import SplashRenderer from 'components/shared/Splash';
import { pseudoStripes } from 'modules/helpers/style';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint, sizes } from 'variables';

const Wrapper = styled.div<{ includeStripes: boolean; hasImage: boolean }>`
    position: relative;
    z-index: 1;
    margin: 0 -${Spacing16};
    color: ${({ theme }) => theme.ColorTextPrimary};

    ${({ hasImage }) =>
        hasImage &&
        css`
            margin: ${Spacing24} -${Spacing16} 0;

            ${breakpoint.up('768px')} {
                margin: ${Spacing24} -${Spacing16};
            }
        `}

    ${({ includeStripes, theme }) =>
        includeStripes &&
        css`
            ${pseudoStripes({ position: 'bottom', height: Spacing12, color1: theme.ColorBackgroundPrimary })}

            ${breakpoint.up('768px')} {
                ${pseudoStripes({
                    position: 'bottom',
                    height: Spacing24,
                    color1: theme.ColorBackgroundPrimary
                })}
            }

            &:before {
                z-index: 0;
                width: 100%;
            }
        `}

    &:after {
        content: '';
        height: ${({ includeStripes }) => (includeStripes ? `calc(100% - ${Spacing12})` : '100%')};
        background: ${({ theme }) => theme.ColorBackgroundPrimary};
        position: absolute;
        top: 0;
        z-index: -1;
        width: 100%;

        ${breakpoint.up('768px')} {
            height: ${({ includeStripes }) => (includeStripes ? `calc(100% - ${Spacing24})` : '100%')};
        }
    }
`;

const Container = styled.div`
    display: grid;
    grid-template-rows: auto auto;

    ${breakpoint.up('768px')} {
        grid-column-gap: ${Spacing32};
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto;
    }

    max-width: 100%;
    margin: 0 auto;
    padding: 0 ${Spacing16};

    ${breakpoint.up(sizes.xl)} {
        max-width: 1230px;
    }

    ${breakpoint.up(sizes.xxl)} {
        max-width: 1400px;
    }

    ${breakpoint.up(sizes.xxxl)} {
        max-width: 1680px;
    }
`;

const TextContainer = styled.div<{ includeStripes: boolean; hasImage: boolean; hasPlusNudgeOffer: boolean }>`
    display: grid;
    grid-template:
        'textContent' auto
        'disclaimerButton' auto
        'countdown' auto
        'plusOfferNudge' auto;
    grid-column-gap: ${({ theme }) => theme.Spacing16};
    padding: ${Spacing24} 0;

    ${breakpoint.up('768px')} {
        padding: ${Spacing48} 0;

        ${({ hasImage, hasPlusNudgeOffer }) =>
            (hasImage &&
                css`
                    min-height: 320px;
                    padding-left: ${Spacing16};
                  `) ||
            (hasPlusNudgeOffer &&
                css`
                    flex-direction: row;
                    justify-content: space-between;
                    grid-column: span 3;
                    grid-template:
                        'textContent countdown plusOfferNudge' auto
                        'disclaimerButton countdown plusOfferNudge' auto;
                  `) ||
            css`
                    flex-direction: row;
                    justify-content: space-between;
                    grid-column: span 2;
                    grid-template:
                        'textContent countdown' auto
                        'disclaimerButton countdown' auto;
                `}
    }

    ${({ includeStripes, hasImage }) =>
        includeStripes &&
        css`
            padding-bottom: calc(${Spacing24} + ${Spacing12});

            ${breakpoint.up('768px')} {
                padding-bottom: calc(${Spacing48} + ${Spacing24});
                ${hasImage && 'min-height: 344px;'}
            }
        `}
`;

const TextContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: center;
    grid-area: textContent;
`;

const ImageContainer = styled.div`
    position: relative;
    margin: -${Spacing24} 0 0 0;
    z-index: 1;

    & img {
        border-radius: ${BorderRadiusMedium};
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    ${breakpoint.up('768px')} {
        margin: -${Spacing24} ${Spacing16} -${Spacing24} 0;
    }
`;

const LabelText = styled(Label)`
    margin-bottom: ${Spacing4};
    opacity: 0.6;
`;

const StyledSplash = styled(SplashRenderer)`
    margin-bottom: ${({ theme }) => theme.Spacing8};
`;

const BodyText = styled(Body)`
    margin-top: ${Spacing16};
    margin-bottom: 0;
`;

const StyledInlinePageActionButton = styled(InlinePageActionButton)`
    margin-top: ${Spacing12};
    
    &, &:hover, &:active {
        color: ${({ theme }) => theme.ColorTextPrimary};
    }

    &:hover {
        text-decoration: none;
    }
`;

const CountdownContainer = styled.div<{ hasImage: boolean }>`
    margin-top: ${Spacing24};
    grid-area: countdown;

    ${({ hasImage }) =>
        !hasImage &&
        css`
            ${breakpoint.up('768px')} {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                margin-top: 0;
            }
        `}
`;

const PlusOfferNugdeContainer = styled.div<{ hasImage: boolean }>`
    margin-top: ${Spacing24};
    grid-area: plusOfferNudge;

    ${breakpoint.up('768px')} {
        max-width: 400px;
    }

    ${({ hasImage }) =>
        !hasImage &&
        css`
            ${breakpoint.up('768px')} {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                margin-top: 0;
            }
        `}
`;

const DisclaimerButtonContainer = styled.div`
    margin-top: ${Spacing16};
    grid-area: disclaimerButton;
`;

const StyledSection = styled(Layout.Section)`
    padding: 0;

    ${breakpoint.up('1025px')} {
        padding: ${({ theme }) => `0 ${theme.Spacing16}`};
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas: 'offerToggle';
    gap: ${({ theme }) => theme.Spacing32};

    ${breakpoint.up('1025px')} {
        grid-template-columns: 1fr 4fr;
        grid-template-areas: 'whiteSpace offerToggle';
    }
`;

interface IExtendedPlaceholderHeaderProps {
    model: ExtendedPlaceholderHeaderModel;
    shouldDisplayAsH1: boolean;
    contentBlockId: string;
    plusOfferNudgeModel?: MatasPlusOfferNudgeModel;
}

export function ExtendedPlaceholderHeader(props: IExtendedPlaceholderHeaderProps): JSX.Element {
    const {
        model: {
            image,
            heading,
            pageAction,
            buttonText,
            bodyText,
            overline,
            includeStripes,
            dataLayer,
            countdown,
            disclaimerText,
            disclaimerButtonText,
            splash,
            personalOfferToggle
        },
        shouldDisplayAsH1,
        contentBlockId,
        plusOfferNudgeModel
    } = props;
    const quickViewId = `ExtendedPlaceholderHeaderQuickView-${contentBlockId}`;

    // If we dont have alt text from CMS, we are setting it to empty string instead of null
    if (image && image.altText === null) {
        image.altText = '';
    }

    return personalOfferToggle ? (
        <StyledSection>
            <Grid>
                <PersonalOfferToggle model={personalOfferToggle} disclaimer={disclaimerText} />
            </Grid>
        </StyledSection>
    ) : (
        <Wrapper includeStripes={includeStripes} hasImage={!!image}>
            <Container data-datalayer={JSON.stringify(dataLayer)}>
                {image && (
                    <ImageContainer>
                        {pageAction ? (
                            <PageAction action={pageAction}>
                                <Picture
                                    {...mapResponsiveImageModelToPictureProps(
                                        image,
                                        [[sizes.xxl, 650], [sizes.xl, 550], [sizes.md, 450], 400],
                                        'WidthScale'
                                    )}
                                />
                            </PageAction>
                        ) : (
                            <Picture
                                {...mapResponsiveImageModelToPictureProps(
                                    image,
                                    [[sizes.xxl, 650], [sizes.xl, 550], [sizes.md, 450], 400],
                                    'WidthScale'
                                )}
                            />
                        )}
                        <DisclaimerButton
                            disclaimerText={disclaimerText}
                            buttonText={disclaimerButtonText}
                            quickViewId={quickViewId}
                        />
                    </ImageContainer>
                )}
                <TextContainer
                    includeStripes={includeStripes}
                    hasImage={!!image}
                    hasPlusNudgeOffer={!!plusOfferNudgeModel}
                >
                    <TextContent>
                        {splash && <StyledSplash splash={splash} />}
                        {overline && <LabelText size="M">{overline}</LabelText>}
                        <Headline as={shouldDisplayAsH1 ? 'h1' : 'h2'} size="XL">
                            {heading}
                        </Headline>
                        {bodyText && <BodyText size="L">{bodyText}</BodyText>}
                        {buttonText && pageAction && (
                            <StyledInlinePageActionButton action={pageAction} fontWeight="semibold">
                                {buttonText}
                            </StyledInlinePageActionButton>
                        )}
                    </TextContent>

                    {!!countdown && (
                        <ThemeProvider theme="default">
                            <CountdownContainer hasImage={!!image}>
                                <Countdown countdown={countdown} />
                            </CountdownContainer>
                        </ThemeProvider>
                    )}

                    {!image && (
                        <DisclaimerButtonContainer>
                            <DisclaimerButton
                                disclaimerText={disclaimerText}
                                buttonText={disclaimerButtonText}
                                quickViewId={quickViewId}
                                relative
                            />
                        </DisclaimerButtonContainer>
                    )}

                    {plusOfferNudgeModel && (
                        <PlusOfferNugdeContainer hasImage={!!image}>
                            <PlusOfferNudge model={plusOfferNudgeModel} />
                        </PlusOfferNugdeContainer>
                    )}
                </TextContainer>
            </Container>
        </Wrapper>
    );
}

export default ExtendedPlaceholderHeader;
